import { Box, type SxProps } from "@mui/material";
import {
  DataGridPremium,
  type GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { merge } from "lodash";
import React, { type ComponentProps, useContext } from "react";
import { ProjectContext } from "../../contexts/ProjectContext.tsx";
import { convertDate } from "../../utils/dateTimeUtils.ts";
import { BaseTableToolbar } from "./BaseTableToolbar.tsx";

export type BaseTableProps<R extends GridValidRowModel> = ComponentProps<
  typeof DataGridPremium<R>
> & {
  loading: boolean;
  highlightField?: string;
  tableHeight?: number | "auto";
  minTableHeight?: number;
  maxTableHeight?: number;
  boxStyling?: SxProps;
  boxClassName?: string;
  tableId: string;
};

export default function BaseTable<R extends GridValidRowModel>({
  loading = false,
  highlightField,
  tableHeight,
  minTableHeight,
  maxTableHeight,
  boxStyling,
  boxClassName,
  tableId,
  ...datagridProps
}: BaseTableProps<R>) {
  const { highlightDate } = useContext(ProjectContext) || {};

  let apiRef = useGridApiRef();

  if (datagridProps.apiRef) {
    apiRef = datagridProps.apiRef;
  }

  // Deep merge the callers partial slotProps with default and required values
  // Note that any field of type array will merge the list values by index, not replace the entire array
  datagridProps.slotProps = merge(
    {
      // Default props
      loadingOverlay: {
        variant: "linear-progress",
      },
    },
    datagridProps.slotProps,
    {
      // Required props
      toolbar: {
        tableId: tableId,
      },
    },
  );

  if (datagridProps?.slots && !datagridProps?.slots?.toolbar) {
    datagridProps.slots.toolbar = BaseTableToolbar;
  }

  return (
    <Box
      className={boxClassName}
      sx={
        boxStyling || {
          height: tableHeight,
          minHeight: minTableHeight || 200,
          maxHeight: tableHeight === "auto" ? maxTableHeight || 500 : undefined,
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
        }
      }
    >
      <DataGridPremium<R>
        apiRef={apiRef}
        showToolbar={true}
        slots={{
          toolbar: BaseTableToolbar,
        }}
        loading={loading}
        getRowHeight={() => "auto"}
        density="compact"
        getRowId={(row) => row.id ?? crypto.randomUUID()}
        getRowClassName={(params) =>
          highlightDate &&
          highlightField &&
          convertDate.dateIsAfterHighlight(
            highlightDate,
            params.row[highlightField],
          )
            ? "MuiDataGrid-row--highlighted"
            : ""
        }
        rowSelection={false}
        pagination
        disableAggregation
        disablePivoting
        disableColumnPinning
        disableRowGrouping
        multipleColumnsSortingMode="always"
        {
          ...datagridProps /* this has to be last so that anything passed in overrides the defaults defined here */
        }
      />
    </Box>
  );
}
